import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../../axiosConfig';
import { Plus, X, ArrowLeft } from 'lucide-react';

const CustomPageBlocks = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const pageId = searchParams.get('pageId') || '';
  const pageName = searchParams.get('pageName') || 'Custom Page';

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    name: '',
    isActive: ''
  });
  const [pageList, setPageList] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const pageSizeOptions = [5, 10, 20, 50];

  const [debouncedFilters, setDebouncedFilters] = useState(filters);

  // URL → state sync
  useEffect(() => {
    const startFromUrl = parseInt(searchParams.get('start'), 10);
    const lengthFromUrl = parseInt(searchParams.get('length'), 10);
    const nameFromUrl = searchParams.get('name') || '';
    const isActiveFromUrl = searchParams.get('isActive') || '';

    const length = pageSizeOptions.includes(lengthFromUrl) ? lengthFromUrl : 10;
    const start = startFromUrl >= 0 ? startFromUrl : 0;
    const page = Math.floor(start / length) + 1;

    setCurrentPage(page);
    setItemsPerPage(length);
    const newFilters = {
      name: nameFromUrl,
      isActive: isActiveFromUrl
    };
    setFilters(newFilters);
    setDebouncedFilters(newFilters);
  }, [searchParams]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedFilters(filters);
    }, 300);
    return () => clearTimeout(timer);
  }, [filters]);

  const updateUrlParams = useCallback((page, size, currentFilters = debouncedFilters) => {
    const params = new URLSearchParams();
    params.set('pageId', pageId);
    params.set('pageName', pageName);
    
    const start = Math.max(0, (page - 1) * size);

    params.set('start', start.toString());
    params.set('length', size.toString());

    if (currentFilters.name) params.set('name', currentFilters.name);
    if (currentFilters.isActive) params.set('isActive', currentFilters.isActive);

    setSearchParams(params, { replace: true });
  }, [setSearchParams, pageId, pageName]);

  const handlePageChange = useCallback((page) => {
    const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      updateUrlParams(page, itemsPerPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [totalItems, itemsPerPage, updateUrlParams]);

  const handlePageSizeChange = useCallback((size) => {
    if (pageSizeOptions.includes(size)) {
      setCurrentPage(1);
      setItemsPerPage(size);
      updateUrlParams(1, size);
    }
  }, [updateUrlParams]);

  const handleFilterChange = useCallback((filterName, value) => {
    const newFilters = { ...filters, [filterName]: value };
    setFilters(newFilters);

    if (filterName !== 'name') {
      setCurrentPage(1);
      setDebouncedFilters(newFilters);
      updateUrlParams(1, itemsPerPage, newFilters);
    }
  }, [filters, itemsPerPage, updateUrlParams]);

  const applyFilters = useCallback(() => {
    setCurrentPage(1);
    setDebouncedFilters(filters);
    updateUrlParams(1, itemsPerPage, filters);
  }, [filters, itemsPerPage, updateUrlParams]);

  const resetFilters = useCallback(() => {
    const emptyFilters = { name: '', isActive: '' };
    setFilters(emptyFilters);
    setDebouncedFilters(emptyFilters);
    setCurrentPage(1);
    updateUrlParams(1, itemsPerPage, emptyFilters);
  }, [itemsPerPage, updateUrlParams]);

  // Fetch blocks filtered by pageMeta (which should match pageName)
  useEffect(() => {
    const fetchCustomPageBlocks = async () => {
      if (currentPage < 1 || itemsPerPage < 1) return;

      try {
        setFilterLoading(true);
        setError(null);

        const response = await axiosInstance.get(
          '/internal-api/v1/client-web/cms/page/page-blocks/list',
          {
            params: {
              start: 0,
              length: 100,
            }
          }
        );

        if (response.data?.data) {
          console.log('🔍 All blocks from API:', response.data.data);
          console.log('🎯 Looking for pageName:', pageName);
          console.log('🎯 Looking for pageId:', pageId);
          
          // Filter blocks by pageName matching the custom page name from URL
          const customPageBlocks = response.data.data.filter(block => {
            console.log('Block:', {
              id: block.id,
              name: block.name,
              pageCode: block.pageCode,
              pageName: block.pageName
            });
            return block.pageCode === "CUSTOM_DESTINATION_VIEW" && 
                   block.pageName === pageName;
          });
          
          console.log('✅ Filtered customPageBlocks:', customPageBlocks);

          let filtered = customPageBlocks;
          if (debouncedFilters.name?.trim()) {
            const searchTerm = debouncedFilters.name.toLowerCase().trim();
            filtered = customPageBlocks.filter(block =>
              (block.name || '').toLowerCase().includes(searchTerm) ||
              (block.blockHeader || '').toLowerCase().includes(searchTerm) ||
              (block.subHeader || '').toLowerCase().includes(searchTerm)
            );
          }

          if (debouncedFilters.isActive !== '') {
            const isActiveBool = debouncedFilters.isActive === 'true';
            filtered = filtered.filter(block => block.isActive === isActiveBool);
          }

          setPageList(filtered);
          setTotalItems(filtered.length);
        } else {
          setPageList([]);
          setTotalItems(0);
        }
      } catch (error) {
        console.error("Error fetching custom page blocks:", error);
        setError("Failed to load custom page blocks. Please try again.");
        setPageList([]);
        setTotalItems(0);
      } finally {
        setFilterLoading(false);
      }
    };

    fetchCustomPageBlocks();
  }, [currentPage, itemsPerPage, debouncedFilters, pageName]);

  const handleRemoveBlock = async (blockId) => {
    if (!window.confirm('Are you sure you want to remove this block?')) return;

    try {
      await axiosInstance.post(
        '/internal-api/v1/client-web/cms/page/page-blocks/state-update',
        {
          id: blockId,
          state: false,
          mode: "REMOVE"
        }
      );

      setPageList(prev => prev.filter(block => block.id !== blockId));
      setTotalItems(prev => prev - 1);

    } catch (error) {
      console.error('Error removing block:', error);
      alert('Failed to remove block. Please try again.');
    }
  };

  const handleStatusToggle = async (blockId, currentIsActive) => {
    const action = currentIsActive ? 'inactive' : 'active';
    const confirmMessage = `Are you sure you want to ${action}?`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      await axiosInstance.post(
        '/internal-api/v1/client-web/cms/page/page-blocks/state-update',
        {
          id: blockId,
          state: !currentIsActive,
          mode: "STATE_UPDATE"
        }
      );

      // Update the block status in the list
      setPageList(prev => prev.map(block => 
        block.id === blockId 
          ? { ...block, isActive: !currentIsActive }
          : block
      ));

    } catch (error) {
      console.error('Error updating block status:', error);
      alert('Failed to update block status. Please try again.');
    }
  };

  const handleBlockTypeSelection = (blockType) => {
    setShowModal(false);
    if (blockType === 'destination-carousel') {
      // Navigate to CustomPageAttractions page with pageMeta for custom page
      const params = new URLSearchParams();
      // pageName is just the actual page name like "Test 2"
      params.set('pageMeta', pageName);
      params.set('pageId', pageId);
      params.set('pageName', pageName);
      params.set('pageCode', 'CUSTOM_DESTINATION_VIEW');
      navigate(`/CustomPageAttractions?${params.toString()}`);
      console.log('Destination Carousel selected for custom page');
    } else if (blockType === 'banner-group') {
      // Navigate to CustomPageBannerGroups with custom page meta
      const params = new URLSearchParams();
      params.set('pageMeta', pageName);
      params.set('pageId', pageId);
      params.set('pageName', pageName);
      params.set('pageCode', 'CUSTOM_DESTINATION_VIEW');
      navigate(`/CustomPageBannerGroups?${params.toString()}`);
      console.log('Banner Group selected for custom page');
    }
  };

  const getStatusBadgeClass = (isActive) => {
    return isActive ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getExpiryRowClass = (validTill) => {
    if (!validTill) return '';

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const expiry = new Date(validTill);
    expiry.setHours(0, 0, 0, 0);

    const diffMs = expiry - today;
    const diffDays = diffMs / (1000 * 60 * 60 * 24);

    if (diffDays <= 7 && diffDays >= 0) {
      return 'bg-red-300 hover:bg-red-400';
    }

    if (diffDays <= 14 && diffDays > 7) {
      return 'bg-orange-300 hover:bg-orange-400';
    }

    return '';
  };

  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const generatePaginationPages = () => {
    const pages = [];
    const maxVisiblePages = 7;
    const sidePages = 2;

    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      pages.push(1);
      let startPage = Math.max(2, currentPage - sidePages);
      let endPage = Math.min(totalPages - 1, currentPage + sidePages);

      if (currentPage <= sidePages + 1) endPage = Math.min(totalPages - 1, maxVisiblePages - 2);
      if (currentPage >= totalPages - sidePages) startPage = Math.max(2, totalPages - maxVisiblePages + 2);

      if (startPage > 2) pages.push('...');
      for (let i = startPage; i <= endPage; i++) pages.push(i);
      if (endPage < totalPages - 1) pages.push('...');
      if (totalPages > 1) pages.push(totalPages);
    }
    return pages;
  };

  const paginationPages = generatePaginationPages();

  if (error) {
    return (
      <div className="flex items-center justify-center flex-1">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col flex-1 text-sm bg-slate-100">
      <div className="px-4 py-4 bg-white border-b sm:px-6 border-slate-200">
        <div className="flex items-center justify-between">
          <h1 className="flex items-center gap-2 text-xl font-semibold text-slate-800">
            📄 {pageName} - Blocks
          </h1>
          <button
            onClick={() => navigate('/BlockList')}
            className="px-3 py-1.5 bg-slate-100 text-slate-600 rounded text-xs sm:text-sm flex items-center gap-1 hover:bg-slate-200 transition-colors"
          >
            <ArrowLeft size={16} /> Back to Pages
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 px-4 py-3 sm:px-6">
        <button
          onClick={() => setShowModal(true)}
          className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs sm:text-sm flex items-center gap-1 hover:bg-blue-700 transition-colors"
        >
          <Plus size={16} /> Add Block 
        </button>
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
          <div className="w-full max-w-md bg-white rounded-lg shadow-xl">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h2 className="text-lg font-semibold text-slate-800">Select Block Type</h2>
              <button
                onClick={() => setShowModal(false)}
                className="transition-colors text-slate-400 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-3">
              <button
                onClick={() => handleBlockTypeSelection('banner-group')}
                className="w-full p-4 text-left transition-all border-2 rounded-lg bg-slate-50 border-slate-200 hover:border-blue-500 hover:bg-blue-50 group"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-12 h-12 text-2xl transition-colors bg-blue-100 rounded-lg group-hover:bg-blue-200">
                    🎨
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-800 group-hover:text-blue-700">Banner Group</h3>
                    <p className="text-xs text-slate-500">Add promotional banners to your page</p>
                  </div>
                </div>
              </button>
              <button
                onClick={() => handleBlockTypeSelection('destination-carousel')}
                className="w-full p-4 text-left transition-all border-2 rounded-lg bg-slate-50 border-slate-200 hover:border-blue-500 hover:bg-blue-50 group"
              >
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-12 h-12 text-2xl transition-colors bg-purple-100 rounded-lg group-hover:bg-purple-200">
                    🎠
                  </div>
                  <div>
                    <h3 className="font-medium text-slate-800 group-hover:text-blue-700">Destination Carousel</h3>
                    <p className="text-xs text-slate-500">Add a carousel of attractions and destinations</p>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 mx-4 mt-4 overflow-hidden bg-white border rounded-lg shadow-sm sm:mx-6 border-slate-200">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div className="flex items-center space-x-2">
            <span className="text-xs text-slate-500">Show</span>
            <select
              value={itemsPerPage}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value, 10))}
              className="h-8 px-2 py-1 text-xs bg-white border rounded border-slate-200 focus:outline-none focus:border-blue-500"
            >
              {pageSizeOptions.map(size => (
                <option key={size} value={size}>{size}</option>
              ))}
            </select>
            <span className="text-xs text-slate-500">entries</span>
          </div>
          <div className="text-xs text-slate-500">
            Total: {totalItems} blocks
          </div>
        </div>

        <div className="relative overflow-x-auto">
          {filterLoading && (
            <div className="absolute inset-0 z-10 flex items-center justify-center bg-white bg-opacity-75">
              <div className="flex items-center space-x-2">
                <div className="w-4 h-4 border-b-2 border-blue-600 rounded-full animate-spin"></div>
                <div className="text-xs text-slate-500">Loading...</div>
              </div>
            </div>
          )}

          <div className="flex gap-4 mt-2 mb-2 ml-2 text-xs text-slate-700">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-red-400 border rounded"></span> Expiring in ≤ 7 days
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-orange-400 border rounded"></span> Expiring in ≤ 14 days
            </span>
          </div>

          <table className="w-full">
            <thead>
              <tr className="h-10 bg-slate-50">
                <th className="px-3 text-xs font-semibold text-left text-slate-800">DISPLAY ORDER</th>
                <th className="px-2 text-xs font-semibold text-left text-slate-800">BLOCK NAME</th>
                <th className="px-2 text-xs font-semibold text-left text-slate-800">BLOCK TYPE</th>
                <th className="px-2 text-xs font-semibold text-left text-slate-800">Valid From</th>
                <th className="px-2 text-xs font-semibold text-left text-slate-800">Valid Till</th>
                <th className="px-2 text-xs font-semibold text-left text-slate-800">STATUS</th>
                <th className="px-2 text-xs font-semibold text-left text-slate-800">ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {pageList.length > 0 ? (
                pageList
                  .slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)
                  .map(page => (
                    <tr
                      key={page.id}
                      className={`h-12 transition-colors border-t border-slate-200 
                        ${getExpiryRowClass(page.validTill)}`}
                    >
                      <td className="px-3 text-xs text-slate-700">{page.displayOrder || '-'}</td>
                      <td className="px-2 text-xs font-medium text-slate-700">{page.name || page.blockHeader || '-'}</td>
                      <td className="px-2 text-xs text-slate-700">{page.blockTypeName || page.blockTypeCode || '-'}</td>
                      <td className="px-2 text-xs text-slate-700">
                        {formatDate(page.validFrom)}
                      </td>
                      <td className="px-2 text-xs text-slate-700">{formatDate(page.validTill)}</td>
                      <td className="px-2">
                        <span 
                          onClick={() => handleStatusToggle(page.id, page.isActive)}
                          className={`inline-block px-2 py-0.5 rounded-full text-xs cursor-pointer hover:opacity-80 transition-opacity ${getStatusBadgeClass(page.isActive)}`}
                          title="Click to toggle status"
                        >
                          {page.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-2">
                        <div className="flex space-x-1">
                          <button 
                            onClick={() => {
                              const params = new URLSearchParams();
                              params.set("blockId", page.id);
                              
                              // Navigate based on block type
                              const blockType = page.blockTypeName || page.blockTypeCode || '';
                              if (blockType === 'DESTINATION CARD CAROUSEL') {
                                navigate(`/AttractionView?${params.toString()}`);
                              } else if (blockType === 'Main Banner Group') {
                                navigate(`/ViewBannerGroup?${params.toString()}`);
                              } else {
                                navigate(`/AttractionView?${params.toString()}`);
                              }
                            }}
                            className="flex items-center justify-center w-6 h-6 text-xs transition-colors border rounded-full bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100"
                            title="View"
                          >
                            👁️
                          </button>
                          <button
                            onClick={() => {
                              const params = new URLSearchParams();
                              params.set("blockId", page.id);
                              params.set("pageMeta", pageName);

                              if (page.validFrom) params.set("validFrom", page.validFrom);
                              if (page.validTill) params.set("validTill", page.validTill);

                              // Navigate based on block type
                              const blockType = page.blockTypeName || page.blockTypeCode || '';
                              if (blockType === 'Main Banner Group') {
                                navigate(`/EditBannerGroup?${params.toString()}`);
                              } else if (blockType === 'DESTINATION CARD CAROUSEL') {
                                navigate(`/AttractionEdit?${params.toString()}`);
                              } else {
                                navigate(`/AttractionEdit?${params.toString()}`);
                              }
                            }}
                            className="flex items-center justify-center w-6 h-6 text-xs transition-colors border rounded-full bg-slate-50 border-slate-200 text-slate-500 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-600"
                            title="Edit Blocks"
                          >
                            ✏️
                          </button>
                          <button
                            onClick={() => handleRemoveBlock(page.id)}
                            className="flex items-center justify-center w-6 h-6 text-xs transition-colors border rounded-full bg-slate-50 border-slate-200 text-slate-500 hover:bg-red-100 hover:text-red-600 hover:border-red-300"
                            title="Remove"
                          >
                            🗑️
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
              ) : (
                <tr>
                  <td colSpan="7" className="px-3 py-8 text-xs text-center text-slate-500">
                    No blocks found for this custom page
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {totalItems > 0 && (
          <div className="flex flex-col items-center justify-between gap-3 p-4 border-t sm:flex-row border-slate-200">
            <div className="text-xs text-slate-500">
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} blocks
            </div>
            <div className="flex items-center space-x-1">
              <button
                disabled={currentPage === 1 || filterLoading}
                onClick={() => handlePageChange(1)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="First page"
              >
                <span className="hidden sm:inline">First</span>
                <span className="sm:hidden">««</span>
              </button>

              <button
                disabled={currentPage === 1 || filterLoading}
                onClick={() => handlePageChange(currentPage - 1)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="Previous page"
              >
                <span className="hidden sm:inline">Previous</span>
                <span className="sm:hidden">‹</span>
              </button>

              <div className="flex items-center space-x-1">
                {paginationPages.map((page, index) => (
                  page === '...' ? (
                    <span key={`ellipsis-${index}`} className="flex items-center justify-center w-8 h-8 text-xs cursor-default text-slate-400">...</span>
                  ) : (
                    <button
                      key={`page-${page}`}
                      onClick={() => handlePageChange(page)}
                      disabled={filterLoading}
                      className={`h-8 w-8 rounded text-xs font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed ${
                        page === currentPage
                          ? 'bg-blue-600 text-white border border-blue-600 shadow-sm'
                          : 'bg-white border border-slate-200 text-slate-600 hover:bg-blue-50 hover:border-blue-300 hover:text-blue-700'
                      }`}
                    >
                      {page}
                    </button>
                  )
                ))}
              </div>

              <button
                disabled={currentPage === totalPages || filterLoading}
                onClick={() => handlePageChange(currentPage + 1)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="Next page"
              >
                <span className="hidden sm:inline">Next</span>
                <span className="sm:hidden">›</span>
              </button>

              <button
                disabled={currentPage === totalPages || filterLoading}
                onClick={() => handlePageChange(totalPages)}
                className="flex items-center h-8 px-2 text-xs transition-colors border rounded border-slate-200 text-slate-500 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-slate-50"
                title="Last page"
              >
                <span className="hidden sm:inline">Last</span>
                <span className="sm:hidden">»»</span>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomPageBlocks;